/**
 * A file-writing tool call as a prominent card — icon, action ("Created" /
 * "Edited"), the file name, and its lime mono path. This is the scannable spine
 * of the thread: the files the agent produced, visible rather than folded into a
 * faint "N steps" row. Click to expand the full ToolDetail.
 */
import { useState } from 'react';
import type { ToolPart } from '~/components/chat/message-parts';
import { fileStat, toolNameOf, toolSummary } from './tool-meta';
import { ToolDetail } from './ToolDetail';
import { openFileInWorkbench } from './file-link';
import { DuneaSpinner } from './DuneaSpinner';

const CREATE_TOOLS = new Set(['write_file', 'bulk_write_files']);

function baseName(p: string | undefined): string {
  if (!p) {
    return 'file';
  }

  const parts = p.replace(/\/+$/, '').split('/');

  return parts[parts.length - 1] || 'file';
}

export function FileCard({ part, isStreaming = false }: { part: ToolPart; isStreaming?: boolean }) {
  const [open, setOpen] = useState(false);
  const name = toolNameOf(part);
  const path = toolSummary(name, part.input); // shortened, display path
  const fullPath = (part.input?.filePath as string | undefined) ?? undefined;
  const isComplete = part.state === 'output-available';
  const isError = part.state === 'output-error';
  const running = !isComplete && !isError && isStreaming;
  const isCreate = CREATE_TOOLS.has(name);
  const action = running ? (isCreate ? 'Creating' : 'Editing') : isCreate ? 'Created' : 'Edited';
  const stat = fileStat(name, part.input);

  return (
    <div className="my-1.5">
      <div
        className="group/fc flex items-center gap-3 rounded-[11px] border border-hairline bg-surface px-3.5 py-3 cursor-pointer transition-colors hover:border-subtle"
        onClick={() => setOpen(!open)}
      >
        <div className="flex-shrink-0 w-[30px] h-[30px] rounded-lg bg-raised grid place-items-center">
          {running ? (
            <DuneaSpinner size={15} className="text-accent" />
          ) : isError ? (
            <span className="i-ph:warning-circle text-danger text-[15px]" />
          ) : (
            <span className="i-ph:file-text text-secondary text-[15px]" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-medium text-secondary">
            <span className={`font-semibold ${isError ? 'text-danger' : isCreate ? 'text-success' : 'text-info'}`}>
              {action}
            </span>{' '}
            {baseName(fullPath ?? path)}
          </div>
          {path && (
            <div
              className="font-geist-mono text-[12.5px] text-accent/85 truncate mt-0.5 hover:text-accent"
              onClick={
                fullPath
                  ? (e) => {
                      e.stopPropagation();
                      openFileInWorkbench(fullPath);
                    }
                  : undefined
              }
            >
              {path}
            </div>
          )}
        </div>
        {isError && <span className="text-[11px] text-danger flex-shrink-0">failed</span>}
        {!isError && stat && (
          <span className="font-geist-mono text-[10.5px] flex-shrink-0 flex items-center gap-1.5">
            {stat.added > 0 && <span className="text-success">+{stat.added}</span>}
            {stat.removed > 0 && <span className="text-danger">−{stat.removed}</span>}
          </span>
        )}
        <span
          className={`i-ph:caret-right text-[13px] text-faint flex-shrink-0 transition-transform ${
            open ? 'rotate-90' : 'opacity-0 group-hover/fc:opacity-100 touch:opacity-100'
          }`}
        />
      </div>
      {open && (
        <div className="ml-5 my-1.5 rounded-lg border border-hairline px-3.5 py-3" onClick={(e) => e.stopPropagation()}>
          <ToolDetail part={part} />
        </div>
      )}
    </div>
  );
}
